/**
 * The **Developer Center** shell (M9 §8 · M8-P4 §2, capture
 * `discovery/ui-reference/developers/INDEX.md`).
 *
 * monday's token page does not live in the board app's settings: it has its own header
 * (`monday Developer Center`) and its own left nav — *My apps* · divider · *API playground* ·
 * **API token** · *API analytics*. The nav is reproduced with its unbuilt entries **disabled
 * and labelled**, the way the add-view gallery lists an unbuilt view kind, rather than hidden:
 * a user who has seen monday's page must not wonder where the entries went.
 *
 * Two changes in round 9v (`specs/M8-P4-developers-docs-playground.md` §2):
 *
 * - **API playground** stops being a disabled placeholder and becomes a page.
 * - **API documentation** is added after it. It is **ours, not monday's** — monday's docs live
 *   on `developer.monday.com`, off the product entirely, and were never captured. It is marked
 *   as ours in its tooltip and registered as a deviation in the spec.
 *
 * The shell is now a layout (`DeveloperShell`) with the token page as one of its children, so
 * all four pages share one nav, one 267 px rail and one 1109 px content pane.
 */
import type * as React from 'react';
import { Link } from 'react-router';
import { ApiTokenPanel } from './ApiTokenPanel';
import type { TokenSource } from './source';
import './developer.css';

export interface DeveloperNavEntry {
  id: string;
  label: string;
  /** `null` when we serve it; otherwise the module that will. */
  plannedIn: string | null;
  /** Where the entry goes. `null` for an entry that is not built yet. */
  to: string | null;
  /** `true` for an entry monday's own Developer Center does not have. */
  ours?: boolean;
  /** monday draws a divider after "My apps". */
  dividerAfter?: boolean;
}

/** monday's own order, verbatim from the capture, plus our one addition after the playground. */
export const DEVELOPER_NAV: readonly DeveloperNavEntry[] = [
  { id: 'apps', label: 'My apps', plannedIn: 'M9.G6', to: null, dividerAfter: true },
  { id: 'playground', label: 'API playground', plannedIn: null, to: '/developers/playground' },
  { id: 'docs', label: 'API documentation', plannedIn: null, to: '/developers/docs', ours: true },
  { id: 'token', label: 'API token', plannedIn: null, to: '/me/developers' },
  { id: 'analytics', label: 'API analytics', plannedIn: 'M9.G6', to: null },
];

export interface DeveloperShellProps {
  /** Which nav entry is the current page. */
  current: string;
  children: React.ReactNode;
}

function navTitle(entry: DeveloperNavEntry): string | undefined {
  if (entry.plannedIn !== null) return `Arrives with ${entry.plannedIn}.`;
  if (entry.ours === true) return 'Ours: monday’s API documentation lives outside the product.';
  return undefined;
}

export function DeveloperShell({ current, children }: DeveloperShellProps): React.ReactElement {
  return (
    <div className="ez-dev" data-testid="developer-center">
      <nav className="ez-dev__nav" aria-label="Developer Center">
        {DEVELOPER_NAV.map((entry) => {
          const selected = entry.id === current;
          const testId = `developer-nav-${entry.id}`;
          return (
            <div key={entry.id}>
              {entry.to === null ? (
                <button
                  type="button"
                  className="ez-dev__nav-item"
                  disabled
                  title={navTitle(entry)}
                  data-testid={testId}
                >
                  {entry.label}
                </button>
              ) : (
                <Link
                  to={entry.to}
                  className="ez-dev__nav-item"
                  aria-current={selected ? 'page' : undefined}
                  title={navTitle(entry)}
                  data-testid={testId}
                >
                  {entry.label}
                </Link>
              )}
              {entry.dividerAfter === true ? <div className="ez-dev__nav-divider" /> : null}
            </div>
          );
        })}
      </nav>
      <main className="ez-dev__pane">{children}</main>
    </div>
  );
}

export interface DeveloperCenterProps {
  /** Forwarded to the token panel; production leaves it undefined and tRPC is used. */
  source?: TokenSource;
}

export function DeveloperCenter({ source }: DeveloperCenterProps = {}): React.ReactElement {
  return (
    <DeveloperShell current="token">
      <ApiTokenPanel source={source} />
    </DeveloperShell>
  );
}
